import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Users, 
    Building2, 
    GraduationCap, 
    CreditCard, 
    TrendingUp, 
    AlertCircle,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    Loader2
} from 'lucide-react';
import { 
    AreaChart, 
    Area, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from 'recharts';

const StatCard = ({ title, value, subValue, icon: Icon, color, trend }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm transition-all hover:shadow-md">
        <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon className="h-6 w-6 text-white" />
            </div>
            {trend && (
                <div className={`flex items-center gap-1 text-xs font-bold ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                    {Math.abs(trend)}%
                </div>
            )}
        </div>
        <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <h3 className="text-2xl font-bold text-slate-900">{value}</h3>
            {subValue && <p className="text-xs text-slate-400 mt-1 font-medium">{subValue}</p>}
        </div>
    </div>
);

const SuperAdminDashboard = () => {
    const [metrics, setMetrics] = useState(null);
    const [activity, setActivity] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [metricsRes, activityRes] = await Promise.all([
                axios.get('/super-admin/metrics'),
                axios.get('/super-admin/activity')
            ]);
            setMetrics(metricsRes.data);
            setActivity(activityRes.data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-[60vh]">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
        </div>
    );

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

    return (
        <div className="space-y-8 p-4">
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Platform Overview</h1>
                <p className="text-slate-500 text-sm mt-1">Real-time performance across all institutions</p>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="Total Institutions" 
                    value={metrics?.institutions?.total} 
                    subValue={`${metrics?.institutions?.newLast30Days} new this month`}
                    icon={Building2} 
                    color="bg-blue-600"
                    trend={12}
                />
                <StatCard 
                    title="Active Subscriptions" 
                    value={metrics?.institutions?.byStatus?.active || 0} 
                    subValue={`${metrics?.institutions?.byStatus?.trial || 0} currently in trial`}
                    icon={TrendingUp} 
                    color="bg-green-600"
                    trend={8}
                />
                <StatCard 
                    title="Monthly Revenue" 
                    value={`KES ${metrics?.revenue?.currentMonth?.toLocaleString()}`} 
                    subValue="Paid this period"
                    icon={CreditCard} 
                    color="bg-indigo-600"
                    trend={15}
                />
                <StatCard 
                    title="At Risk / Overdue" 
                    value={`KES ${metrics?.revenue?.overdue?.total?.toLocaleString()}`} 
                    subValue={`${metrics?.revenue?.overdue?.count} pending bills`}
                    icon={AlertCircle} 
                    color="bg-red-600"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Growth Chart */}
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">Institution Growth</h3>
                            <p className="text-xs text-slate-400 font-medium">New signups over the last 12 months</p>
                        </div>
                        <div className="flex gap-2">
                            <span className="px-3 py-1 bg-slate-50 text-[10px] font-bold text-slate-500 rounded-lg">Last 12 Months</span>
                        </div>
                    </div>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={metrics?.charts?.monthlySignups}>
                                <defs>
                                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis 
                                    dataKey="month" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{fontSize: 10, fill: '#94a3b8'}} 
                                    dy={10}
                                />
                                <YAxis 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{fontSize: 10, fill: '#94a3b8'}} 
                                />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="count" 
                                    stroke="#3b82f6" 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorCount)" 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Status Breakdown */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-900 mb-6">Subscription Status</h3>
                    <div className="space-y-6">
                        {Object.entries(metrics?.institutions?.byStatus || {}).map(([status, count], idx) => {
                            const total = metrics?.institutions?.total || 1;
                            const percentage = Math.round((count / total) * 100);
                            return (
                                <div key={status} className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="capitalize font-semibold text-slate-700">{status}</span>
                                        <span className="font-bold text-slate-900">{count} ({percentage}%)</span>
                                    </div>
                                    <div className="w-full bg-slate-100 rounded-full h-2">
                                        <div 
                                            className="h-2 rounded-full transition-all duration-1000" 
                                            style={{ 
                                                width: `${percentage}%`,
                                                backgroundColor: COLORS[idx % COLORS.length]
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-10 p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        <div className="flex items-center gap-3">
                            <AlertCircle className="h-5 w-5 text-amber-500" />
                            <p className="text-xs text-slate-600 leading-relaxed">
                                <span className="font-bold">{metrics?.trials?.expiringSoon} trial subscriptions</span> will expire within the next 7 days.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Signups */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-slate-50 flex items-center justify-between">
                        <h3 className="font-bold text-slate-900">Recent Institution Signups</h3>
                        <button className="text-xs font-bold text-blue-600 hover:underline">View All</button>
                    </div>
                    <div className="divide-y divide-slate-50">
                        {activity?.recentInstitutions?.map((inst) => (
                            <div key={inst.id} className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-bold text-slate-500">
                                    {inst.name[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-slate-900 truncate">{inst.name}</p>
                                    <p className="text-xs text-slate-400">{inst.city}, {inst.country}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-bold text-slate-900">{new Date(inst.createdAt).toLocaleDateString()}</p>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                                        inst.subscription?.status === 'active' ? 'bg-green-100 text-green-700' : 
                                        inst.subscription?.status === 'trial' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                                    }`}>
                                        {inst.subscription?.status || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pending Actions */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-slate-50 flex items-center justify-between">
                        <h3 className="font-bold text-slate-900">Critical Alerts</h3>
                        <div className="flex gap-1">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            <span className="text-[10px] font-bold text-red-500 uppercase tracking-widest">Priority</span>
                        </div>
                    </div>
                    <div className="p-0">
                        <div className="bg-amber-50/50 p-4 border-b border-amber-100">
                            <p className="text-xs font-bold text-amber-800 flex items-center gap-2">
                                <Calendar className="h-4 w-4" /> Expiring Trials
                            </p>
                        </div>
                        <div className="divide-y divide-slate-50 max-h-[300px] overflow-y-auto">
                            {activity?.expiringTrials?.map((trial) => (
                                <div key={trial.id} className="p-4 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-bold text-slate-900">{trial.institution?.name}</p>
                                        <p className="text-xs text-slate-400">Expires {new Date(trial.trialEndDate).toLocaleDateString()}</p>
                                    </div>
                                    <button className="text-xs font-bold px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">
                                        Extend
                                    </button>
                                </div>
                            ))}
                            {activity?.expiringTrials?.length === 0 && (
                                <div className="p-8 text-center text-slate-400 text-sm">No trials expiring soon</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;
